import Table from "@/common/elements/Table";
import { InmateDetailsTableContainer } from "@/components/InmateDetails/styles";
import DistinguishingCharacteristicsForm from "./DistinguishingCharacteristicsForm";
import { StyledFlex, StyledGridCol, StyledGridRow } from "@/common/styles";
import Button from "@/common/elements/Button";
import Modal from "@/common/elements/Modal";
import { forwardRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import { Popconfirm } from "antd";
import { v4 as uuidv4 } from "uuid";
import { hasCheckSecurity, isNonEmptyVariable } from "@/util/common_function";
import API_ENDPOINTS from "@/util/api";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const DISTINGUISHING_CHARACTERISTICS_HEADERS = [
  { key: "characteristicType", text: "Characteristic Type" },
  { key: "nist", text: "NIST" },
  { key: "location", text: "Location" },
  { key: "description", text: "Description" },
];

const DistinguishingCharacteristics = forwardRef(
  ({ personalFormDistinguishingRef, bioData, fieldsEnabled, onBioChange, startingPoint = "" }, ref) => {
    const dispatch = useDispatch();
    const { bookings, bookingsFieldsEnabled } = useSelector(
      (state) => state?.bookings
    );

    // "Config" — drag-reorder/resize columns, persisted per-user (see
    // docs/plans/grid-column-config-feature.md). Shared across modules
    // (Bookings, Warrants, ...) — gridId must carry the caller's module
    // context via startingPoint, same fix as AddressesList.jsx, or two
    // different screens end up sharing one saved column layout.
    const {
      headers: dcHeaders,
      isConfiguring,
      configButtonLabel,
      startConfiguring,
      confirmSave,
      discardChanges,
      dataTableRef,
    } = useGridColumnConfig(
      `${startingPoint || "Bookings"}:DistinguishingCharacteristics`,
      DISTINGUISHING_CHARACTERISTICS_HEADERS
    );

    const [showModal, setShowModal] = useState(false);
    const [selectDC, setSelectDC] = useState(null);
    const [dcData, setDCData] = useState([]);
    const [showEdit, setShowEdit] = useState(false);
    const [empty, setEmpty] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    useEffect(() => {
      const dc = bioData?.distinguishingCharacteristics;
      const dca = dc?.map((d) => ({ ...d, uuid: uuidv4() }));
      setDCData(() => dca);
    }, [bioData]);  

    const openModal = () => {
      setShowModal(() => true);
    };

    const closeModal = () => {
      setSelectDC(() => null);
      setShowModal(() => false);
      setShowEdit(() => false);
    };

    const selectDCDataFn = (value) => {
      setSelectDC(() => value);
      setShowEdit(() => true);
      setEmpty(() => true);
    };

    const addDc = () => {
      setSelectDC(() => null);
      setShowEdit(() => false);
      openModal();
    };

    const editDC = () => {
      openModal();
      setShowEdit(true);
    };

    const deleteDC = () => {
      let dc = dcData?.filter((p) => p?.uuid !== selectDC?.uuid);

      onBioChange({ distinguishingCharacteristics: dc });

      if (isNonEmptyVariable(selectDC?.distinguishingCharacteristicsID)) {
        const url =
          API_ENDPOINTS?.distinguishingCharacteristics +
          "/id/" +
          selectDC?.distinguishingCharacteristicsID;
        dispatch(setDeleteAPis(url));
      }

      closeModal();
    };

    const saveClick = () => {
      // console.log("On Save Click calls");
      personalFormDistinguishingRef?.current
        ?.validateFields()
        .then((values) => {
          let dc = dcData || [];
          const images = personalFormDistinguishingRef?.current?.getImgData();
          let updateValues = {
            ...values,
            distinguishingCharacteristicsPhotos: images?.map((i) => {
              if (isNonEmptyVariable(i?.photoId)) {
                const obj = selectDC?.distinguishingCharacteristicsPhotos?.find(
                  (d) => d?.photoId === i?.photoId
                );
                return {
                  ...obj,
                  photos: [i],
                };
              }
              return {
                photos: [i],
              };
            }),
          };
          if (showEdit) {
            dc = dc?.map((d) => {
              if (d?.uuid === selectDC?.uuid) {
                return {
                  ...d,
                  ...updateValues,
                };
              }

              return d;
            });
          } else {
            dc.push(updateValues);
          }

          onBioChange({ distinguishingCharacteristics: dc });
          personalFormDistinguishingRef?.current?.setImgDataEmpty();
          personalFormDistinguishingRef?.current?.setSelectedImgEmpty();
          personalFormDistinguishingRef?.current?.resetForm();
          closeModal();
        })
        .catch((err) => {
          console.error("Validation Error:", err);
        });
    };

    const cancelAction = () => {
      setSelectDC(() => null);
      setShowEdit(() => false);
      setShowModal(() => false);
    };
    useEffect(() => {
      if (showEdit) {
        let isShow = hasCheckSecurity(
          Security_Types.functionality,
          Entity_types.subModule,
          LABELS_CAPITAL.MODIFY,
          MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
          SUB_MODULES_LIST_WITH_ID.Arrest_Report
        );
        if (isShow) {
          setIsDisabled(false);
        } else {
          setIsDisabled(true);
        }
      } else {
        setIsDisabled(false);
      }
    }, [showEdit]);
    return (
      <>
        {/* {hasCheckSecurity(
          Security_Types.functionality,
          Entity_types.module,
          LABELS_CAPITAL.ADD,
          MODULES_LIST_WITH_ID.MODULE_BOOKINGS
        ) && ( */}
        <DistinguishingCharacteristicsForm
          isQuickAdd={true}
          formData={empty ? null : selectDC}
          ref={personalFormDistinguishingRef}
          quickSave={saveClick}
          bookingsFieldsEnabled={bookingsFieldsEnabled}
        />
        {/* )} */}

        <InmateDetailsTableContainer>
          {/* Header */}
          <StyledGridRow justify="end">
            <StyledGridCol style={{ paddingBottom: "15px" }}>
              <StyledFlex gap="10px">
                <Button
                  onClick={addDc}
                  disabled={
                    !bookingsFieldsEnabled ||
                    isConfiguring ||
                    !hasCheckSecurity(
                      Security_Types.functionality,
                      Entity_types.subModule,
                      LABELS_CAPITAL.ADD,
                      MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                      SUB_MODULES_LIST_WITH_ID.Arrest_Report
                    )
                  }
                >
                  Add
                </Button>

                <Button
                  disabled={
                    !showEdit ||
                    isConfiguring ||
                    !hasCheckSecurity(
                      Security_Types.functionality,
                      Entity_types.subModule,
                      LABELS_CAPITAL.VIEW,
                      MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                      SUB_MODULES_LIST_WITH_ID.Arrest_Report
                    )
                  }
                  onClick={editDC}
                >
                  Details
                </Button>

                <Button
                  disabled={!showEdit || isConfiguring}
                  onClick={cancelAction}
                >
                  Cancel
                </Button>

                <Popconfirm
                  title="Delete the Distinguishing Characteristics"
                  description="Are you sure to delete this Distinguishing Characteristics?"
                  onConfirm={deleteDC}
                  onCancel={() => {}}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    disabled={
                      !showEdit ||
                      !bookingsFieldsEnabled ||
                      isConfiguring ||
                      !hasCheckSecurity(
                        Security_Types.functionality,
                        Entity_types.subModule,
                        LABELS_CAPITAL.DELETE,
                        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                        SUB_MODULES_LIST_WITH_ID.Arrest_Report
                      )
                    }
                  >
                    Delete
                  </Button>
                </Popconfirm>

                {/* Config — drag-reorder/resize columns; only while Modify is active */}
                {!isConfiguring ? (
                  <Button
                    htmlType="button"
                    onClick={startConfiguring}
                  >
                    {configButtonLabel}
                  </Button>
                ) : (
                  <Popconfirm
                    title="Do you want to save the current list view configuration?"
                    onConfirm={confirmSave}
                    onCancel={discardChanges}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button htmlType="button">
                      {configButtonLabel}
                    </Button>
                  </Popconfirm>
                )}
              </StyledFlex>
            </StyledGridCol>
          </StyledGridRow>

          {hasCheckSecurity(
            Security_Types.functionality,
            Entity_types.subModule,
            LABELS_CAPITAL.VIEW,
            MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
            SUB_MODULES_LIST_WITH_ID.Arrest_Report
          ) && (
            <Table
              ref={dataTableRef}
              headers={dcHeaders}
              draggable={isConfiguring}
              data={dcData}
              onRowClick={selectDCDataFn}
              onRowDoubleClick={(value) => {
                selectDCDataFn(value);
                editDC();
              }}
              selected={selectDC === null}
            />
          )}
        </InmateDetailsTableContainer>

        {showModal && (
          <Modal
            header="Distinguishing Characteristics"
            show={showModal}
            designPattern="ant"
            onHide={closeModal}
            handleSubmit={saveClick}
            handleCancel={closeModal}
            width={"50%"}
            submitButtonDisabled={bookingsFieldsEnabled}
            hideSubmitButton={isDisabled}
          >
            <DistinguishingCharacteristicsForm
              isQuickAdd={false}
              formData={selectDC}
              ref={personalFormDistinguishingRef}
              bookingsFieldsEnabled={bookingsFieldsEnabled}
              isEdit={showEdit}
            />
          </Modal>
        )}
      </>
    );
  }
);
export default DistinguishingCharacteristics;
